/**
 * Query hooks over the two basket routes (tasks B1).
 *
 * Filters live in the URL, via `usePaginatedQuery`, so an estate view is
 * linkable — "the four SIMs I mean are on this page" has to survive being
 * pasted into a chat window.
 */

import type { BasketCounts, BasketSummary, Paginated, PlanSummary, SimSummary } from '@rd/shared';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { apiPaginated, apiRequest } from '../../lib/apiClient.js';
import { usePaginatedQuery, type PaginatedQueryResult } from '../../lib/paginated.js';

export interface SimFilterState {
  simStatus?: string | undefined;
  /**
   * Which identifier to search, or **undefined to search both** — the default,
   * and what the search box sends unless the operator pins a field.
   *
   * The two spellings were found by probing the live gateway. The vendor
   * documents "MSISDN / SIM"; `SIM` returns 400, and so do ICCID, SIMNO,
   * SIMNUMBER and every casing of them. The value that works for a SIM number
   * is the undocumented `SIM_NO`. Both match on a substring, which is why
   * neither length nor a leading `89` can tell the two apart here — the API
   * settles it by trying one and falling back to the other.
   */
  simFilterType?: 'MSISDN' | 'SIM_NO' | undefined;
  filterValue?: string | undefined;
  brandName?: string | undefined;
  activatedPlanType?: string | undefined;
}

/**
 * The API's maximum page size, which is also more baskets than any estate we
 * have seen (the largest account holds 16).
 *
 * It must not exceed the cap: asking for 200 made every basket request 400,
 * which on this screen reads as "choose a basket" with no basket to choose.
 */
const ALL_BASKETS_PAGE_SIZE = 100;

/**
 * Every basket in one call — deliberately NOT paginated.
 *
 * `usePaginatedQuery` reads the shared `page` URL parameter, so making this a
 * paginated query put two lists on one screen fighting over one value: pressing
 * Next on the SIM table sent the BASKET query to page 2, which for a 3-basket
 * estate is empty, so the selected basket resolved to null and the SIM list
 * stopped loading without a request or an error. The basket list is a dropdown
 * with no pagination UI; it never needed a page of its own.
 */
export function useBaskets(): UseQueryResult<Paginated<BasketSummary>, unknown> {
  return useQuery<Paginated<BasketSummary>>({
    queryKey: ['baskets', 'all'],
    queryFn: () =>
      apiPaginated<BasketSummary>('/baskets', {
        query: { page: 1, pageSize: ALL_BASKETS_PAGE_SIZE },
      }),
  });
}

/**
 * The reserved ref meaning "every basket I can see" — the API's
 * `ALL_BASKETS_REF`, which the SIM listing route answers to alongside real
 * basket refs.
 *
 * An estate split across three baskets is still one estate. Looking for a
 * subscriber number should not mean picking each basket in turn and searching
 * three times.
 */
export const ALL_BASKETS_REF = 'bsk_all';

const EMPTY_COUNTS: BasketCounts = {
  total: 0,
  active: 0,
  available: 0,
  testMode: 0,
  safeCustody: 0,
  inactive: 0,
  inProgress: 0,
  suspended: 0,
};

/**
 * The merged estate as a basket-shaped option, so the picker and the status
 * filter can treat it like any other choice.
 *
 * The counts are summed here rather than fetched because the picker already
 * holds every visible basket: the aggregate is arithmetic on data in hand, and
 * asking the network for a number we can add up would only make the screen
 * slower and the two figures capable of disagreeing.
 */
export function allBasketsOption(items: BasketSummary[]): BasketSummary {
  return {
    basketRef: ALL_BASKETS_REF,
    parentBasketRef: null,
    basketName: 'All baskets',
    remarks: null,
    createdAt: null,
    managedBy: null,
    ownerEmail: null,
    ownerFirstName: null,
    ownerLastName: null,
    permission: null,
    counts: items.reduce<BasketCounts>(
      (acc, b) => ({
        total: acc.total + (b.counts?.total ?? 0),
        active: acc.active + (b.counts?.active ?? 0),
        available: acc.available + (b.counts?.available ?? 0),
        testMode: acc.testMode + (b.counts?.testMode ?? 0),
        safeCustody: acc.safeCustody + (b.counts?.safeCustody ?? 0),
        inactive: acc.inactive + (b.counts?.inactive ?? 0),
        inProgress: acc.inProgress + (b.counts?.inProgress ?? 0),
        suspended: acc.suspended + (b.counts?.suspended ?? 0),
      }),
      EMPTY_COUNTS,
    ),
  };
}

export function useBasketSims(
  basketRef: string | null,
  filters: SimFilterState,
): PaginatedQueryResult<SimSummary> {
  return usePaginatedQuery<SimSummary>({
    key: ['baskets', basketRef, 'sims', filters],
    path: `/baskets/${basketRef ?? ''}/sims`,
    // Undefined entries are dropped by `apiClient.buildUrl`, so an unset filter
    // is an absent parameter rather than an empty one the API would have to
    // interpret.
    query: {
      ...(filters.simStatus ? { simStatus: filters.simStatus } : {}),
      /**
       * A value may travel alone; a field never may.
       *
       * The absence of `simFilterType` is now a meaningful request — "match
       * either identifier" — which is exactly what the form shows when nothing
       * is pinned, so the wire and the visible state still agree. What the
       * form must never send is a field with no value: picking "SIM number"
       * from the dropdown before typing anything sent `simFilterType` by
       * itself, which the upstream rejects outright (422), so merely choosing
       * what to search by, before searching for anything, replaced the table
       * with an error. An empty search is not a search; it is the unfiltered
       * list.
       */
      ...(filters.filterValue
        ? {
            filterValue: filters.filterValue,
            ...(filters.simFilterType ? { simFilterType: filters.simFilterType } : {}),
          }
        : {}),
      ...(filters.brandName ? { brandName: filters.brandName } : {}),
      ...(filters.activatedPlanType ? { activatedPlanType: filters.activatedPlanType } : {}),
    },
    enabled: basketRef !== null,
  });
}

export function useUpgradablePlans(basketRef: string | null) {
  return useQuery<SimSummary[]>({
    queryKey: ['baskets', basketRef, 'upgradable'],
    queryFn: () => apiRequest<SimSummary[]>(`/baskets/${basketRef ?? ''}/sims/upgradable`),
    enabled: basketRef !== null,
  });
}

/**
 * `enabled: false` is how the activation screen avoids fetching plans at all
 * when the tenant has a default one — a tenant that never shows a plan should
 * not be calling the vendor's plan catalogue on every visit, and may not even
 * be entitled to it.
 */
export function usePlans(options?: { enabled?: boolean }) {
  return useQuery<PlanSummary[]>({
    queryKey: ['plans'],
    queryFn: () => apiRequest<PlanSummary[]>('/plans'),
    enabled: options?.enabled ?? true,
  });
}
